import type { LocationResponse } from "@safeplate/contracts";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  Disclosure,
  Input,
  Label,
  Select,
} from "@safeplate/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, MapPin, Pencil, Plus } from "lucide-react";
import { useId, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState } from "../components/states.js";
import { useShell } from "../shell/shell-context.js";
import { createLocation, updateLocation } from "./setup-api.js";

interface LocationFormValue {
  address: Record<string, string>;
  council_area: string | null;
  email: string | null;
  name: string;
  phone: string | null;
  status: "active" | "setup";
  timezone: string;
  trading_name: string | null;
}

function readForm(form: FormData): LocationFormValue {
  const text = (key: string) => String(form.get(key) ?? "").trim();
  const nullable = (key: string) => text(key) || null;
  return {
    address: {
      ...(text("line1") ? { line1: text("line1") } : {}),
      ...(text("suburb") ? { suburb: text("suburb") } : {}),
      ...(text("city") ? { city: text("city") } : {}),
      ...(text("postcode") ? { postcode: text("postcode") } : {}),
    },
    council_area: nullable("council_area"),
    email: nullable("email"),
    name: text("name"),
    phone: nullable("phone"),
    status: text("status") === "setup" ? "setup" : "active",
    timezone: text("timezone") || "Pacific/Auckland",
    trading_name: nullable("trading_name"),
  };
}

const NZ_TIMEZONES = [
  { label: "New Zealand", value: "Pacific/Auckland" },
  { label: "Chatham Islands", value: "Pacific/Chatham" },
];

// Keeps a location's existing zone selectable even if it isn't a NZ one.
function timezoneOptions(current?: string) {
  return current && !NZ_TIMEZONES.some((zone) => zone.value === current)
    ? [...NZ_TIMEZONES, { label: current, value: current }]
    : NZ_TIMEZONES;
}

function locationSummary(location: LocationResponse): string {
  const city = location.address?.city;
  return [typeof city === "string" ? city : "", location.council_area ?? ""]
    .filter(Boolean)
    .join(" · ");
}

function LocationEditor({
  current,
  onClose,
}: {
  current?: LocationResponse;
  onClose: () => void;
}) {
  const shell = useShell();
  const queryClient = useQueryClient();
  const prefix = useId();
  const save = useMutation({
    mutationFn: (input: LocationFormValue) =>
      current
        ? updateLocation(current.id, input)
        : createLocation({ ...input, licensable: true }),
    onSuccess: async () => {
      await shell.refreshLocations();
      await queryClient.invalidateQueries({
        queryKey: ["locations", shell.activeOrganisationId],
      });
      onClose();
    },
  });
  const address = current?.address ?? {};
  const field = (
    name: string,
    label: string,
    value?: string | null,
    type = "text",
  ) => (
    <div>
      <Label htmlFor={`${prefix}-${name}`}>{label}</Label>
      <Input
        className="mt-1.5"
        defaultValue={value ?? ""}
        id={`${prefix}-${name}`}
        name={name}
        type={type}
      />
    </div>
  );
  const addressField = (
    name: "city" | "line1" | "postcode" | "suburb",
    label: string,
  ) => field(name, label, address[name] ?? "");

  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={save.isPending} form={prefix} type="submit">
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      size="lg"
      title={current ? "Edit location" : "Add location"}
    >
      <form
        id={prefix}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          save.mutate(readForm(new FormData(event.currentTarget)));
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor={`${prefix}-name`}>Name</Label>
            <Input
              className="mt-1.5"
              defaultValue={current?.name ?? ""}
              id={`${prefix}-name`}
              name="name"
              required
            />
          </div>
          {addressField("line1", "Street address")}
          {addressField("city", "City")}
        </div>
        <Disclosure className="mt-4" summary="More details">
          <div className="grid gap-4 pt-2 sm:grid-cols-2">
            {field("trading_name", "Trading name", current?.trading_name)}
            {addressField("suburb", "Suburb")}
            {addressField("postcode", "Postcode")}
            {field("council_area", "Council area", current?.council_area)}
            {field("phone", "Phone", current?.phone)}
            {field("email", "Email", current?.email, "email")}
            <div>
              <Label htmlFor={`${prefix}-timezone`}>Time zone</Label>
              <Select
                className="mt-1.5"
                defaultValue={current?.timezone ?? "Pacific/Auckland"}
                id={`${prefix}-timezone`}
                name="timezone"
              >
                {timezoneOptions(current?.timezone).map((zone) => (
                  <option key={zone.value} value={zone.value}>
                    {zone.label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor={`${prefix}-status`}>Status</Label>
              <Select
                className="mt-1.5"
                defaultValue={current?.status === "setup" ? "setup" : "active"}
                id={`${prefix}-status`}
                name="status"
              >
                <option value="setup">Still setting up</option>
                <option value="active">Active</option>
              </Select>
            </div>
          </div>
        </Disclosure>
        {save.error ? (
          <p className="mt-4 text-sm font-semibold text-red-800" role="alert">
            {displayError(save.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

export function LocationPanel({
  autoAdd = false,
  canManage,
}: {
  autoAdd?: boolean;
  canManage: boolean;
}) {
  const shell = useShell();
  const [editing, setEditing] = useState<LocationResponse | "new" | null>(
    autoAdd && canManage ? "new" : null,
  );
  const archive = useMutation({
    mutationFn: (id: string) => updateLocation(id, { status: "archived" }),
    onSuccess: () => shell.refreshLocations(),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle>Locations</CardTitle>
        {canManage && shell.locations.length > 0 ? (
          <Button onClick={() => setEditing("new")} size="sm" variant="outline">
            <Plus className="size-4" /> Add location
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="pt-3">
        {shell.locations.length > 0 ? (
          <div className="-mx-5 -mb-5 divide-y divide-stone-200 border-t border-stone-200">
            {shell.locations.map((location) => (
              <div
                className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center"
                key={location.id}
              >
                <MapPin
                  aria-hidden="true"
                  className="hidden size-4 shrink-0 text-stone-500 sm:block"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-stone-950">
                    {location.name}
                  </p>
                  {locationSummary(location) ? (
                    <p className="mt-0.5 text-xs text-stone-500">
                      {locationSummary(location)}
                    </p>
                  ) : null}
                </div>
                {canManage ? (
                  <div className="flex gap-1">
                    <Button
                      onClick={() => setEditing(location)}
                      size="sm"
                      variant="ghost"
                    >
                      <Pencil className="size-3.5" /> Edit
                    </Button>
                    <Button
                      disabled={archive.isPending}
                      onClick={() => {
                        if (globalThis.confirm("Archive this location?"))
                          archive.mutate(location.id);
                      }}
                      size="sm"
                      variant="ghost"
                    >
                      <Archive className="size-3.5" /> Archive
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            action={
              canManage ? (
                <Button onClick={() => setEditing("new")}>
                  <Plus className="size-4" /> Add location
                </Button>
              ) : undefined
            }
            title="Add your first location"
          />
        )}
        {editing ? (
          <LocationEditor
            {...(editing === "new" ? {} : { current: editing })}
            onClose={() => setEditing(null)}
          />
        ) : null}
        {archive.error ? (
          <p className="mt-3 text-sm font-semibold text-red-800" role="alert">
            {displayError(archive.error)}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
